<template>
  <div class="flex h-screen bg-gray-50">
    <!-- Conversations List -->
    <div class="w-80 bg-white border-l border-gray-200">
      <div class="p-6 border-b border-gray-200">
        <h2 class="text-xl font-bold text-gray-800 mb-4">المحادثات</h2>
        <div class="relative">
          <Search class="absolute right-3 top-3 w-5 h-5 text-gray-400" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="البحث في المحادثات..."
            class="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div class="overflow-y-auto">
        <div
          v-for="conversation in filteredConversations"
          :key="conversation.id"
          @click="selectConversation(conversation)"
          :class="[
            'p-4 border-b border-gray-100 cursor-pointer transition-colors',
            selectedConversation?.id === conversation.id ? 'bg-blue-50' : 'hover:bg-gray-50'
          ]"
        >
          <div class="flex items-start space-x-3 space-x-reverse">
            <div class="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
              {{ conversation.participant.name.charAt(0) }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between mb-1">
                <h3 class="font-semibold text-gray-800 truncate">{{ conversation.participant.name }}</h3>
                <span class="text-xs text-gray-500">{{ formatTime(conversation.lastMessage.time) }}</span>
              </div>
              <p class="text-sm text-gray-600 truncate">{{ conversation.lastMessage.content }}</p>
            </div>
            <div class="flex flex-col items-end">
              <div v-if="conversation.unreadCount > 0" class="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center mb-1">
                {{ conversation.unreadCount }}
              </div>
              <div class="w-2 h-2 rounded-full" :class="conversation.participant.isOnline ? 'bg-green-500' : 'bg-gray-300'"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Chat Area -->
    <div class="flex-1 flex flex-col">
      <div v-if="!selectedConversation" class="flex items-center justify-center h-full">
        <div class="text-center">
          <MessageSquare class="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 class="text-lg font-semibold text-gray-800 mb-2">اختر محادثة</h3>
          <p class="text-gray-600">اختر محادثة من القائمة لبدء المراسلة</p>
        </div>
      </div>

      <div v-else class="flex-1 flex flex-col">
        <!-- Chat Header -->
        <div class="bg-white border-b border-gray-200 px-6 py-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3 space-x-reverse">
              <div class="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                {{ selectedConversation.participant.name.charAt(0) }}
              </div>
              <div>
                <h1 class="text-lg font-semibold text-gray-800">{{ selectedConversation.participant.name }}</h1>
                <p class="text-sm text-gray-600">{{ selectedConversation.participant.isOnline ? 'متصل' : 'غير متصل' }}</p>
              </div>
            </div>
            <div class="flex items-center space-x-2 space-x-reverse">
              <button class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <Phone class="w-5 h-5 text-gray-600" />
              </button>
              <button class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <Video class="w-5 h-5 text-gray-600" />
              </button>
              <button class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                <MoreVertical class="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        <!-- Messages -->
        <div class="flex-1 overflow-y-auto p-6 space-y-4" ref="messagesContainer">
          <div
            v-for="message in selectedConversation.messages"
            :key="message.id"
            class="flex"
            :class="message.sender === 'me' ? 'justify-end' : 'justify-start'"
          >
            <div
              class="max-w-xs lg:max-w-md px-4 py-3 rounded-lg"
              :class="message.sender === 'me' 
                ? 'bg-blue-600 text-white rounded-br-sm' 
                : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'"
            >
              <p class="text-sm whitespace-pre-wrap">{{ message.content }}</p>
              <p class="text-xs mt-1 opacity-70">{{ formatTime(message.time) }}</p>
            </div>
          </div>
        </div>

        <!-- Input Area -->
        <div class="border-t border-gray-200 bg-white px-6 py-4">
          <div class="flex items-center space-x-2 space-x-reverse">
            <button class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <Paperclip class="w-5 h-5 text-gray-600" />
            </button>
            <input
              v-model="messageInput"
              type="text"
              placeholder="اكتب رسالة..."
              class="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              @keypress="handleKeyPress"
            />
            <button class="p-2 rounded-lg hover:bg-gray-100 transition-colors">
              <Smile class="w-5 h-5 text-gray-600" />
            </button>
            <button
              @click="sendMessage"
              :disabled="!messageInput.trim()"
              class="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <Send class="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import { Search, Phone, Video, MoreVertical, Paperclip, Smile, Send } from 'lucide-vue-next'

interface Message {
  id: string
  content: string
  sender: 'me' | 'other'
  time: Date
}

interface Participant {
  name: string
  isOnline: boolean
}

interface Conversation {
  id: string
  participant: Participant
  lastMessage: Message
  unreadCount: number
  messages: Message[]
}

const searchQuery = ref('')
const messageInput = ref('')
const selectedConversation = ref<Conversation | null>(null)
const messagesContainer = ref<HTMLElement>()

const conversations: Conversation[] = [
  {
    id: '1',
    participant: { name: 'المحامي أحمد خليل', isOnline: true },
    lastMessage: {
      id: 'msg1',
      content: 'thank you for the consultation, i will review the documents',
      sender: 'other',
      time: new Date(Date.now() - 1000 * 60 * 5)
    },
    unreadCount: 2,
    messages: [
      {
        id: 'msg1',
        content: 'مرحباً، أحتاج استشارة قانونية بخصوص قضيتي',
        sender: 'me',
        time: new Date(Date.now() - 1000 * 60 * 30)
      },
      {
        id: 'msg2',
        content: 'بالتأكيد، أنا هنا للمساعدة. ما هي قضيتك؟',
        sender: 'other',
        time: new Date(Date.now() - 1000 * 60 * 25)
      },
      {
        id: 'msg3',
        content: 'شكراً للاستشارة، سأراجع المستندات',
        sender: 'other',
        time: new Date(Date.now() - 1000 * 60 * 5)
      }
    ]
  },
  {
    id: '2',
    participant: { name: 'مكتب المحاماة الهدف', isOnline: false },
    lastMessage: {
      id: 'msg4',
      content: 'الاجتماع مجدول لغداً الساعة 10 صباحاً',
      sender: 'me',
      time: new Date(Date.now() - 1000 * 60 * 60)
    },
    unreadCount: 0,
    messages: []
  }
]

const filteredConversations = computed(() => {
  if (!searchQuery.value.trim()) return conversations
  
  const query = searchQuery.value.toLowerCase()
  return conversations.filter(conv =>
    conv.participant.name.toLowerCase().includes(query) ||
    conv.lastMessage.content.toLowerCase().includes(query)
  )
})

const selectConversation = (conversation: Conversation) => {
  selectedConversation.value = conversation
  conversation.unreadCount = 0
}

const sendMessage = () => {
  if (!messageInput.value.trim() || !selectedConversation.value) return

  const newMessage: Message = {
    id: `msg-${Date.now()}`,
    content: messageInput.value.trim(),
    sender: 'me',
    time: new Date()
  }

  selectedConversation.value.messages.push(newMessage)
  selectedConversation.value.lastMessage = newMessage
  
  messageInput.value = ''
  
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

const handleKeyPress = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    sendMessage()
  }
}

const formatTime = (date: Date) => {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  
  if (diff < 1000 * 60) return 'now'
  if (diff < 1000 * 60 * 60) return `${Math.floor(diff / (1000 * 60))}m`
  if (diff < 1000 * 60 * 60 * 24) return `${Math.floor(diff / (1000 * 60 * 60))}h`
  return date.toLocaleDateString('ar-SA')
}
</script>
